/**
 * AI Host Control Center
 *
 * Main dashboard for restaurants using AI Host phone ordering service.
 *
 * Features:
 * - Real-time order queue display
 * - Today's performance metrics
 * - Service toggle (pause/resume)
 * - Active calls monitoring
 * - Phone number management
 * - Stripe payment status
 *
 * Author: Dine AI
 * Created: November 20, 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { Phone, Clock, Loader, CheckCircle, CreditCard, Settings, RefreshCw, Sparkles, Sliders, Edit, ArrowRight, MoreVertical, XCircle, AlertTriangle, Calendar, Table2, HelpCircle, MessageSquare, ChevronDown, ChevronUp, Search, X, UtensilsCrossed, Star, ChefHat, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { usePageTitle } from '@/hooks/usePageTitle';
import OrderQueueCard from './OrderQueueCard';
import MenuFilterCard from './MenuFilterCard';
import EventsCard from './EventsCard';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { getUserDisplayInfo } from '@/utils/jwt';
import { apiClient } from '@/services/api/apiClient';
import AIHostSetupWizard from '../setup/AIHostSetupWizard';
// AIHostSettingsPage replaced by full-page /virtual-host/settings route
import FAQManagementModal from './FAQManagementModal';

interface DashboardStats {
  orders_today: number;
  revenue_today: number;
  calls_today: number;
  average_order_value: number;
  orders_pending: number;
  orders_in_progress: number;
  orders_active: number;  // Combined: pending + in_progress (for 2-tab system)
  orders_completed_today: number;
  service_active: boolean;
}

/**
 * Format number with thousand separators and optional decimals
 */
const formatNumber = (num: number, decimals: number = 0): string => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format currency with thousand separators
 */
const formatCurrency = (amount: number): string => {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

interface UsageData {
  billing_period_start: string;
  billing_period_end: string;
  plan_name: string;
  plan_display_name: string;
  minutes_included: number;
  minutes_used: number;
  minutes_remaining: number;
  usage_percent: number;
  overage_minutes: number;
  overage_cost: number;
  transaction_count: number;
  transaction_fees: number;
  base_subscription_fee: number;
  estimated_bill: number;
  alerts: string[];
  account_pending?: boolean;  // True for pending_activation accounts (Sales Portal preview)
}

interface VirtualHostControlCenterProps {
  sidebarExpanded?: boolean;
  hideHeader?: boolean;  // When true, don't render DashboardHeader (used by VHStaffDashboard)
  isStaff?: boolean;     // Indicates VH Staff context
}

const VirtualHostControlCenter: React.FC<VirtualHostControlCenterProps> = ({
  sidebarExpanded = false,
  hideHeader = false,
  isStaff = false
}) => {
  usePageTitle('vhDashboard');
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [restaurantData, setRestaurantData] = useState<any>(null);
  const [hasMultipleLocations, setHasMultipleLocations] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [refreshingUsage, setRefreshingUsage] = useState(false);

  // Settings data (wait time, etc.)
  const [waitTimeMinutes, setWaitTimeMinutes] = useState<number>(15);

  // Accepting Orders state
  const [acceptingOrders, setAcceptingOrders] = useState<boolean>(true);
  const [notAcceptingMessage, setNotAcceptingMessage] = useState<string>('');
  const [notAcceptingUntil, setNotAcceptingUntil] = useState<string>('');
  const [showAcceptingOrdersModal, setShowAcceptingOrdersModal] = useState(false);
  const [savingAcceptingOrders, setSavingAcceptingOrders] = useState(false);

  // Edit Menu dropdown state
  const [isEditMenuDropdownOpen, setIsEditMenuDropdownOpen] = useState(false);
  const editMenuButtonRef = useRef<HTMLButtonElement>(null);
  const editMenuDropdownRef = useRef<HTMLDivElement>(null);

  // Cache rebuild modal state
  const [showCacheRebuildModal, setShowCacheRebuildModal] = useState(false);
  const [isRebuildingCache, setIsRebuildingCache] = useState(false);

  // Sync AI Host state (VH preferences cache rebuild)
  const [syncingAIHost, setSyncingAIHost] = useState(false);

  // Menu highlights modal state
  const [highlightModalOpen, setHighlightModalOpen] = useState(false);
  const [highlightModalCategory, setHighlightModalCategory] = useState<'popular' | 'chef' | 'spice'>('popular');
  const [highlightToggles, setHighlightToggles] = useState<Record<string, boolean>>({});
  const [highlightSearchText, setHighlightSearchText] = useState('');
  const [savingHighlights, setSavingHighlights] = useState(false);

  // Active menu data
  const [activeMenu, setActiveMenu] = useState<any>(null);

  // Setup status tracking
  const [setupStatus, setSetupStatus] = useState<'incomplete' | 'stripe_pending' | 'active'>('incomplete');
  const [hasPhone, setHasPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [hasStripeConnected, setHasStripeConnected] = useState(false);
  const [stripeAccountStatus, setStripeAccountStatus] = useState<'not_started' | 'pending' | 'verified'>('not_started');
  const [paymentProvider, setPaymentProvider] = useState<'stripe' | 'none' | string>('stripe');

  // Current date/time for display
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Modal state
  const [isSetupWizardOpen, setIsSetupWizardOpen] = useState(false);
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  // FAQ data
  const [faqCount, setFaqCount] = useState(0);
  const [topFAQs, setTopFAQs] = useState<Array<{ faq_id: number; question: string; answer: string }>>([]);
  const [allFAQs, setAllFAQs] = useState<Array<{ faq_id: number; question: string; answer: string }>>([]);
  const [faqsExpanded, setFaqsExpanded] = useState(false);
  const [expandedFaqId, setExpandedFaqId] = useState<number | null>(null);

  // Subscription availability for settings menu
  const [virtualAssistantAvailable, setVirtualAssistantAvailable] = useState(false);

  // Check if this is a demo user
  const isDemoUser = localStorage.getItem('isDemo') === 'true';

  // Get user info from auth token
  const authToken = Cookies.get('auth_token');
  let userInfo = null;

  if (!isDemoUser && authToken) {
    try {
      userInfo = getUserDisplayInfo(authToken);
    } catch (e) {
      console.error('Failed to parse auth token:', e);
    }
  }

  const restaurantName = restaurantData?.restaurant_name || "";
  const ownerFirstName = restaurantData?.owner_first_name || userInfo?.firstName || userInfo?.fullName?.split(' ')[0] || 'Restaurant';
  const ownerLastName = restaurantData?.owner_last_name || userInfo?.lastName || '';
  const ownerName = restaurantData?.owner_full_name || userInfo?.fullName || 'Restaurant Owner';

  /**
   * Fetch Virtual Host setup status
   */
  const fetchSetupStatus = async () => {
    try {
      const response = await apiClient.get('/virtual-host/setup/status');
      const data = response.data;

      // Determine setup status based on backend response
      // Backend returns: phone_number_configured, phone_configured (alias), stripe_connected, stripe_status, payment_provider
      const phoneProvisioned = data.phone_number_configured || data.phone_configured || false;
      const provisionedPhoneNumber = data.phone_number || null;
      const stripeConnected = data.stripe_connected || false;
      const stripeStatus = data.stripe_status || 'not_started'; // 'not_started', 'pending', 'verified'
      const provider = data.payment_provider || 'stripe'; // 'stripe', 'none', etc.

      console.log('Setup status:', { phoneProvisioned, stripeConnected, stripeStatus, provider, data });

      setHasPhone(phoneProvisioned);
      setPhoneNumber(provisionedPhoneNumber);
      setHasStripeConnected(stripeConnected);
      setStripeAccountStatus(stripeStatus);
      setPaymentProvider(provider);

      // Calculate overall setup status
      // If payment_provider is 'none', Stripe is not required
      const stripeRequired = provider === 'stripe';

      if (!phoneProvisioned) {
        // Phone not provisioned - incomplete
        setSetupStatus('incomplete');
      } else if (stripeRequired && !stripeConnected) {
        // Stripe required but not connected
        setSetupStatus('incomplete');
      } else if (stripeRequired && stripeStatus === 'pending') {
        // Stripe connected but pending verification
        setSetupStatus('stripe_pending');
      } else if ((stripeRequired && stripeStatus === 'verified') || !stripeRequired) {
        // Either Stripe verified OR Stripe not required (pay at pickup only)
        setSetupStatus('active');
      } else {
        setSetupStatus('incomplete');
      }
    } catch (error) {
      console.error('Error fetching setup status:', error);
      // Default to incomplete if fetch fails
      setSetupStatus('incomplete');
    }
  };

  /**
   * Fetch restaurant profile data
   */
  const fetchRestaurantProfile = async () => {
    try {
      const response = await apiClient.get('/restaurants/profile');
      const data = response.data;

      setRestaurantData(data);

      // Set active menu from profile
      if (data.active_menu) {
        setActiveMenu(data.active_menu);
      }

      // Check for multiple locations
      if (data.all_locations && data.all_locations.length > 1) {
        setHasMultipleLocations(true);
        setLocations(data.all_locations);

        // Set current location based on rest_id
        const current = data.all_locations.find((loc: any) => loc.restaurant_id === data.rest_id);
        setCurrentLocation(current || data.all_locations[0]);
      } else {
        setHasMultipleLocations(false);
        setCurrentLocation(null);
      }
    } catch (error) {
      console.error('Error fetching restaurant profile:', error);
    }
  };

  /**
   * Handle location switch
   */
  const handleLocationSwitch = (location: any) => {
    // Reload page with new rest_id
    window.location.href = `/restaurant-dashboard?rest_id=${location.restaurant_id}`;
  };

  /**
   * Fetch dashboard stats from API
   */
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/virtual-host/control/stats', {
        credentials: 'include'  // Send httpOnly cookie automatically
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch usage data from API
   */
  const fetchUsageData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshingUsage(true);
    }

    // For manual refresh, ensure animation shows for at least 600ms
    const startTime = Date.now();

    try {
      const response = await fetch('/api/virtual-host/control/usage', {
        credentials: 'include'  // Send httpOnly cookie automatically
      });

      if (!response.ok) {
        throw new Error('Failed to fetch usage data');
      }

      const data = await response.json();
      setUsageData(data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoadingUsage(false);

      // Ensure minimum animation time for manual refresh
      if (isRefresh) {
        const elapsed = Date.now() - startTime;
        const minAnimationTime = 600;
        if (elapsed < minAnimationTime) {
          await new Promise(resolve => setTimeout(resolve, minAnimationTime - elapsed));
        }
        setRefreshingUsage(false);
      }
    }
  };

  /**
   * Handle manual refresh of usage data
   */
  const handleRefreshUsage = () => {
    if (!refreshingUsage) {
      fetchUsageData(true);
    }
  };

  /**
   * Fetch AI Host settings (wait time, accepting orders, etc.)
   */
  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/virtual-host/settings');
      const data = response.data;

      // Update wait time from settings
      if (data.wait_time_minutes) {
        setWaitTimeMinutes(data.wait_time_minutes);
      }

      // Update accepting orders state
      setAcceptingOrders(data.accepting_orders !== false);
      setNotAcceptingMessage(data.not_accepting_message || '');
      setNotAcceptingUntil(data.not_accepting_until || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Keep default values
    }
  };

  /**
   * Handle Accepting Orders toggle
   */
  const handleAcceptingOrdersToggle = () => {
    if (acceptingOrders) {
      // Turning OFF - show modal for message and resume time
      setShowAcceptingOrdersModal(true);
    } else {
      // Turning ON - immediately resume
      handleResumeOrders();
    }
  };

  /**
   * Handle resume orders (turn accepting orders back ON)
   */
  const handleResumeOrders = async () => {
    setSavingAcceptingOrders(true);
    try {
      await apiClient.put('/virtual-host/settings', {
        accepting_orders: true,
        not_accepting_message: null,
        not_accepting_until: null
      });
      setAcceptingOrders(true);
      setNotAcceptingMessage('');
      setNotAcceptingUntil('');
      toast.success('Now accepting orders!');
    } catch (error) {
      console.error('Error resuming orders:', error);
      toast.error('Failed to resume orders. Please try again.');
    } finally {
      setSavingAcceptingOrders(false);
    }
  };

  /**
   * Handle pause orders (turn accepting orders OFF)
   */
  const handlePauseOrders = async () => {
    if (!notAcceptingMessage.trim()) {
      toast.error('Please enter a message for customers');
      return;
    }

    setSavingAcceptingOrders(true);
    try {
      await apiClient.put('/virtual-host/settings', {
        accepting_orders: false,
        not_accepting_message: notAcceptingMessage,
        not_accepting_until: notAcceptingUntil || null
      });
      setAcceptingOrders(false);
      setShowAcceptingOrdersModal(false);
      toast.success('Orders paused. Customers will hear your message.');
    } catch (error) {
      console.error('Error pausing orders:', error);
      toast.error('Failed to pause orders. Please try again.');
    } finally {
      setSavingAcceptingOrders(false);
    }
  };

  /**
   * Fetch FAQ data for card preview
   */
  const fetchFAQs = async () => {
    try {
      const response = await apiClient.get('/virtual-host/faqs');
      const data = response.data;
      setFaqCount(data.count || 0);
      const all = (data.faqs || []).map((faq: any) => ({
        faq_id: faq.faq_id,
        question: faq.question,
        answer: faq.answer || ''
      }));
      setAllFAQs(all);
      setTopFAQs(all.slice(0, 3));
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      // Default to empty state
      setFaqCount(0);
      setTopFAQs([]);
    }
  };

  /**
   * Fetch navigation state to determine subscription availability
   * Used for settings menu - Team Management only shows for Virtual Assistant users
   */
  const fetchNavigationState = async () => {
    try {
      const response = await fetch('/api/dashboard/navigation', {
        credentials: 'include'  // Send httpOnly cookie automatically
      });

      if (response.ok) {
        const data = await response.json();
        setVirtualAssistantAvailable(data.virtual_assistant_available || false);
      }
    } catch (error) {
      console.error('Error fetching navigation state:', error);
      // Default to false - Team Management won't show
    }
  };

  /**
   * Toggle Virtual Host service on/off
   */
  const handleToggleService = async () => {
    if (!stats) return;

    const newStatus = !stats.service_active;
    const confirmMessage = newStatus
      ? 'Activate AI Host? Customers will be able to place orders via phone.'
      : 'Pause AI Host? New incoming calls will not be accepted.';

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setToggling(true);

      const response = await fetch('/api/virtual-host/control/toggle-service', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // Send httpOnly cookie automatically
        body: JSON.stringify({
          is_active: newStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to toggle service');
      }

      // Refresh stats
      await fetchStats();

      alert(newStatus ? 'AI Host activated!' : 'AI Host paused.');
    } catch (error) {
      console.error('Error toggling service:', error);
      alert('Failed to toggle service. Please try again.');
    } finally {
      setToggling(false);
    }
  };

  /**
   * Handle Edit Menu navigation
   */
  const handleEditMenu = () => {
    if (activeMenu?.menu_id) {
      const restId = currentLocation?.restaurant_id || restaurantData?.rest_id || localStorage.getItem('current_rest_id');
      navigate(`/restaurant-menu-review?processing_id=${activeMenu.menu_id}&rest_id=${restId}`);
    } else {
      navigate('/restaurant-menu-upload');
    }
  };

  /**
   * Open menu highlights modal for a category
   */
  const openHighlightModal = (category: 'popular' | 'chef' | 'spice') => {
    const highlights = activeMenu?.menu_highlights;
    if (!highlights?.all_items) return;

    // Initialize toggles from current data
    const field = category === 'popular' ? 'is_popular' : category === 'chef' ? 'chef_reccom' : 'spice_adjustable';
    const toggles: Record<string, boolean> = {};
    highlights.all_items.forEach((item: any) => {
      toggles[item.id] = item[field] || false;
    });

    setHighlightToggles(toggles);
    setHighlightModalCategory(category);
    setHighlightSearchText('');
    setHighlightModalOpen(true);
  };

  /**
   * Save highlight toggles
   */
  const saveHighlightToggles = async () => {
    const highlights = activeMenu?.menu_highlights;
    if (!highlights?.all_items || !activeMenu?.menu_id) return;

    const field = highlightModalCategory === 'popular' ? 'is_popular' : highlightModalCategory === 'chef' ? 'chef_reccom' : 'spice_adjustable';

    // Find items that changed
    const changedItems = highlights.all_items.filter((item: any) => {
      const currentVal = item[field] || false;
      const newVal = highlightToggles[item.id] || false;
      return currentVal !== newVal;
    });

    if (changedItems.length === 0) {
      setHighlightModalOpen(false);
      return;
    }

    setSavingHighlights(true);
    try {
      let successCount = 0;
      for (const item of changedItems) {
        try {
          await apiClient.put(`/menus/${activeMenu.menu_id}/items/${item.id}`, {
            [field]: highlightToggles[item.id]
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to update ${item.name}:`, err);
        }
      }

      // Update local state to reflect changes immediately
      const updatedAllItems = highlights.all_items.map((item: any) => ({
        ...item,
        [field]: highlightToggles[item.id] ?? item[field]
      }));

      const updatedHighlights = {
        ...highlights,
        all_items: updatedAllItems,
        popular_count: updatedAllItems.filter((i: any) => i.is_popular).length,
        popular_items: updatedAllItems.filter((i: any) => i.is_popular).map((i: any) => ({ id: i.id, name: i.name })),
        chef_count: updatedAllItems.filter((i: any) => i.chef_reccom).length,
        chef_items: updatedAllItems.filter((i: any) => i.chef_reccom).map((i: any) => ({ id: i.id, name: i.name })),
        spice_count: updatedAllItems.filter((i: any) => i.spice_adjustable).length,
        spice_items: updatedAllItems.filter((i: any) => i.spice_adjustable).map((i: any) => ({ id: i.id, name: i.name })),
      };

      setActiveMenu((prev: any) => ({
        ...prev,
        menu_highlights: updatedHighlights
      }));

      toast.success(`Updated ${successCount} item${successCount > 1 ? 's' : ''}`, { duration: 3000 });
      setHighlightModalOpen(false);
    } catch (error) {
      console.error('Error saving highlights:', error);
      toast.error('Failed to save changes');
    } finally {
      setSavingHighlights(false);
    }
  };

  /**
   * Handle cache rebuild
   */
  const handleRebuildCache = async () => {
    if (!activeMenu?.menu_id) {
      toast.error('No active menu found');
      return;
    }

    setIsRebuildingCache(true);
    setShowCacheRebuildModal(false);

    try {
      const response = await apiClient.post(`/menus/${activeMenu.menu_id}/rebuild-cache`);

      if (response.data.success) {
        toast.success('Menu refreshed successfully!', {
          description: `${response.data.items_cached} menu items updated.`,
          duration: 5000,
        });
      } else {
        toast.error('Failed to refresh menu', {
          description: response.data.message || 'Unknown error occurred',
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('Failed to rebuild cache:', error);
      toast.error('Failed to refresh menu', {
        description: error.response?.data?.detail || error.message || 'Please try again later',
        duration: 5000,
      });
    } finally {
      setIsRebuildingCache(false);
      setIsEditMenuDropdownOpen(false);
    }
  };

  /**
   * Sync AI Host - Rebuilds VH preferences cache
   * Use when SMS/payments aren't working or after settings changes
   */
  const handleSyncAIHost = async () => {
    // Get rest_account_id from restaurant data
    const restAccountId = restaurantData?.rest_account_id;
    if (!restAccountId) {
      toast.error('Restaurant not found');
      return;
    }

    setSyncingAIHost(true);

    try {
      const response = await apiClient.post(`/admin/vh-cache/rebuild/${restAccountId}`);

      if (response.data.success) {
        toast.success('AI Host synced!', {
          description: 'Settings have been refreshed.',
          duration: 3000,
        });
      } else {
        toast.error('Sync failed', {
          description: response.data.message || 'Please try again',
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('Failed to sync AI Host:', error);
      toast.error('Sync failed', {
        description: error.response?.data?.detail || 'Please try again later',
        duration: 5000,
      });
    } finally {
      setSyncingAIHost(false);
    }
  };

  // Click outside handler for edit menu dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editMenuButtonRef.current &&
        !editMenuButtonRef.current.contains(event.target as Node) &&
        editMenuDropdownRef.current &&
        !editMenuDropdownRef.current.contains(event.target as Node)
      ) {
        setIsEditMenuDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initial load
  useEffect(() => {
    fetchRestaurantProfile();
    fetchStats();
    fetchUsageData();
    fetchSetupStatus();
    fetchSettings();
    fetchNavigationState();  // Fetch subscription availability for settings menu
    fetchFAQs();              // Fetch FAQ count for card preview

    // Auto-refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);

    return () => clearInterval(interval);
  }, []);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-gray-500">Failed to load dashboard statistics.</p>
          <button
            onClick={fetchStats}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1">
      {/* Fixed Header - hidden when used by VHStaffDashboard (which has its own header) */}
      {!hideHeader && (
        <DashboardHeader
          restaurantName={restaurantName}
          hasMultipleLocations={hasMultipleLocations}
          currentLocation={currentLocation}
          locations={locations}
          restaurantData={restaurantData}
          userEmail={restaurantData?.email || userInfo?.email || 'User'}
          userName={`${ownerFirstName} ${ownerLastName}`.trim() || ''}
          isDemoUser={isDemoUser}
          onLocationSwitch={handleLocationSwitch}
          sidebarExpanded={sidebarExpanded}
          virtualAssistantAvailable={virtualAssistantAvailable}
          welcomeName={ownerFirstName}
          isStaff={isStaff || userInfo?.isStaff || false}
        />
      )}

      {/* Scrollable Content Area - Light Mode */}
      {/* pt-24 when DashboardHeader is shown (fixed header), pt-6 when hideHeader (VH Staff has own header) */}
      <div className={`flex-1 overflow-y-auto ${hideHeader ? 'pt-6' : 'pt-24'} bg-gray-50`}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Title */}
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">AI Host Control Center</h2>
            <span className="text-gray-400 text-sm">
              {currentDateTime.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              })} • {currentDateTime.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </span>
          </div>

          {/* Two-Card Layout: AI Minutes + Service Controls */}
          {/* Service Controls always visible (for sales demos), AI Minutes hidden for pending accounts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Left Card: AI Call Minutes - ONLY for active accounts */}
            {!loadingUsage && usageData && !usageData.account_pending ? (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{
                        rotate: [0, 10, -10, 10, 0],
                        scale: [1, 1.1, 1, 1.1, 1]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatDelay: 3
                      }}
                    >
                      <Sparkles className="h-5 w-5 text-gray-400 group-hover:text-yellow-500 transition-colors" />
                    </motion.div>
                    <h3 className="text-lg font-semibold text-gray-900">AI Host Phone Line</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status Chip */}
                    {setupStatus === 'incomplete' ? (
                      <button
                        onClick={() => setIsSetupWizardOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 border border-orange-300 hover:bg-orange-200 transition-colors"
                      >
                        <span className="relative flex h-2 w-2">
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                        </span>
                        <span className="text-xs font-semibold text-orange-700">Setup</span>
                      </button>
                    ) : setupStatus === 'stripe_pending' ? (
                      <button
                        onClick={() => setIsSetupWizardOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-100 border border-yellow-300 hover:bg-yellow-200 transition-colors"
                      >
                        <span className="relative flex h-2 w-2">
                          <span className="animate-pulse relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                        </span>
                        <span className="text-xs font-semibold text-yellow-700">Pending</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 border border-green-200">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <span className="text-xs font-semibold text-green-700">Live</span>
                      </div>
                    )}
                    <button
                      onClick={handleRefreshUsage}
                      disabled={refreshingUsage}
                      className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all border border-gray-200"
                      title="Refresh usage data"
                    >
                      <RefreshCw className={`h-4 w-4 text-gray-600 ${refreshingUsage ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Plan Badge + AI Host Line */}
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                  <span className={`
                    px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1
                    ${usageData.plan_name === 'premium_virtual_host'
                      ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 border border-yellow-300'
                      : usageData.plan_name === 'standard_virtual_host'
                      ? 'bg-purple-100 text-purple-700 border border-purple-200'
                      : 'bg-blue-100 text-blue-700 border border-blue-200'
                    }
                  `}>
                    {usageData.plan_display_name}
                  </span>
                  {phoneNumber && (
                    <span className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-green-100 text-green-700 border border-green-200">
                      <Phone className="h-3 w-3" />
                      {phoneNumber.replace(/(\+1)(\d{3})(\d{3})(\d{4})/, '($2) $3-$4')}
                    </span>
                  )}
                </div>

                {/* Minutes Display */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    <motion.span
                      key={usageData.minutes_used}
                      initial={{ scale: 1.1 }}
                      animate={{ scale: 1 }}
                      className="text-3xl font-bold text-gray-900"
                    >
                      {usageData.minutes_used.toLocaleString()}
                    </motion.span>
                    <span className="text-xl text-gray-500">/ {usageData.minutes_included.toLocaleString()}</span>
                    <span className="text-sm text-gray-400 ml-1">minutes</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">
                    {usageData.usage_percent.toFixed(1)}% used this billing cycle
                  </p>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(usageData.usage_percent, 100)}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className={`h-full rounded-full ${
                        usageData.usage_percent >= 95
                          ? 'bg-red-500'
                          : usageData.usage_percent >= 80
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                      }`}
                    />
                  </div>
                </div>

                {/* Minutes Remaining */}
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-all">
                  <div className="flex items-center justify-between">
                    <p className="text-gray-700 font-medium">Minutes Remaining</p>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{usageData.minutes_remaining.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Overage Warning */}
                {usageData.overage_minutes > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3"
                  >
                    <p className="text-red-600 text-xs font-medium">
                      Overage: {usageData.overage_minutes} min (${usageData.overage_cost.toFixed(2)})
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              /* Placeholder for pending accounts - Show Phone Number Card */
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">AI Host Phone Line</h3>
                  </div>
                  {/* Status Chip */}
                  {setupStatus === 'incomplete' ? (
                    <button
                      onClick={() => setIsSetupWizardOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 border border-orange-300 hover:bg-orange-200 transition-colors"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                      </span>
                      <span className="text-xs font-semibold text-orange-700">Setup</span>
                    </button>
                  ) : setupStatus === 'stripe_pending' ? (
                    <button
                      onClick={() => setIsSetupWizardOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-100 border border-yellow-300 hover:bg-yellow-200 transition-colors"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-pulse relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                      </span>
                      <span className="text-xs font-semibold text-yellow-700">Pending</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 border border-green-200">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span className="text-xs font-semibold text-green-700">Live</span>
                    </div>
                  )}
                </div>

                {/* Phone Number Display - Large and prominent */}
                {phoneNumber ? (
                  <div className="mb-4">
                    <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                      <p className="text-gray-500 text-xs mb-1">Assigned Phone Number</p>
                      <p className="text-2xl font-bold text-green-700">
                        {phoneNumber.replace(/(\+1)(\d{3})(\d{3})(\d{4})/, '($2) $3-$4')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200 mb-4">
                    <p className="text-yellow-700 text-sm">Phone number not yet assigned</p>
                  </div>
                )}

                {/* Demo Mode Notice */}
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-blue-700 text-sm font-medium">Preview Mode</p>
                      <p className="text-blue-600 text-xs mt-1">
                        AI Minutes and billing will appear once the account is activated.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Right Card: Service Controls - ALWAYS visible */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 group/card"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Sliders className="h-5 w-5 text-gray-400 group-hover/card:text-purple-600 transition-colors" />
                  <h3 className="text-lg font-semibold text-gray-900">Service Controls</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSyncAIHost}
                    disabled={syncingAIHost}
                    className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Sync AI Host"
                  >
                    <RefreshCw className={`h-4 w-4 ${syncingAIHost ? 'animate-spin' : ''}`} />
                  </button>
                  {/* Settings Gear - Links to AI Host Settings */}
                  <button
                    onClick={() => navigate('/virtual-host/settings')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-yellow-100 border border-gray-200 hover:border-yellow-300 text-gray-600 hover:text-yellow-700 transition-all"
                    title="AI Host Settings"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="text-xs font-semibold">AI HOST</span>
                  </button>
                </div>
              </div>

              {/* Accepting Orders Toggle */}
              <div className="mb-6">
                <div
                  onClick={handleAcceptingOrdersToggle}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer ${
                    acceptingOrders
                      ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      : 'bg-red-50 border-red-200 hover:bg-red-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${acceptingOrders ? 'bg-green-100' : 'bg-red-100'}`}>
                      {acceptingOrders ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">Accepting Orders</p>
                      {acceptingOrders ? (
                        <p className="text-gray-500 text-xs">Click to pause orders</p>
                      ) : notAcceptingUntil ? (
                        <p className="text-yellow-600 text-xs">
                          Resumes at {new Date(notAcceptingUntil).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      ) : (
                        <p className="text-red-500 text-xs">Orders paused - click to resume</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${acceptingOrders ? 'text-green-600' : 'text-red-600'}`}>
                      {acceptingOrders ? 'ON' : 'OFF'}
                    </span>
                    <div className={`w-12 h-7 rounded-full relative transition-all ${
                      acceptingOrders
                        ? 'bg-green-200'
                        : 'bg-red-200'
                    }`}>
                      <div className={`absolute top-1 w-5 h-5 rounded-full transition-all ${
                        acceptingOrders
                          ? 'right-1 bg-green-500'
                          : 'left-1 bg-red-500'
                      }`}></div>
                    </div>
                  </div>
                </div>

                {/* Warning message when orders paused */}
                {!acceptingOrders && notAcceptingMessage && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <p className="text-yellow-700 text-xs">{notAcceptingMessage}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Wait Time - Data-driven from settings */}
              <div>
                <div
                  onClick={() => navigate('/virtual-host/settings#hours')}
                  className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-all cursor-pointer group/wait"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 group-hover/wait:bg-orange-100 rounded-lg transition-colors">
                        <Clock className="h-5 w-5 text-gray-400 group-hover/wait:text-orange-600 transition-colors" />
                      </div>
                      <p className="text-gray-900 font-medium">Wait Time</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900">{waitTimeMinutes}</p>
                      <p className="text-gray-500 text-sm">minutes</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>


          {/* Stats Cards - Funnel order: Calls → Orders → Revenue → AOV */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Calls Today - Primary metric, clickable to open Call Log */}
        <div
          onClick={() => navigate('/call-log')}
          className="relative bg-white rounded-2xl border border-purple-200 shadow-sm p-6 cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-all group"
        >
          {/* Pulsing dot indicator */}
          <span className="absolute top-3 right-3 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
          </span>
          <p className="text-4xl font-bold text-gray-900 mb-2">{formatNumber(stats.calls_today)}</p>
          <p className="text-gray-500 flex items-center justify-between">
            <span>Calls Today</span>
            <span className="text-purple-600 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
              View All
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </p>
        </div>

        {/* Orders Today */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 hover:bg-gray-50 transition-colors">
          <p className="text-4xl font-bold text-gray-900 mb-2">{formatNumber(stats.orders_today)}</p>
          <p className="text-gray-500">Orders Today</p>
        </div>

        {/* Revenue Today */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 hover:bg-gray-50 transition-colors">
          <p className="text-4xl font-bold text-gray-900 mb-2">${formatCurrency(stats.revenue_today)}</p>
          <p className="text-gray-500">Revenue Today</p>
        </div>

        {/* Average Order Value */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 hover:bg-gray-50 transition-colors">
          <p className="text-4xl font-bold text-gray-900 mb-2">${formatCurrency(stats.average_order_value)}</p>
          <p className="text-gray-500">Avg Order Value</p>
        </div>
      </div>

      {/* Order Status & Edit Menu - Two Card Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left Card: Order Status (2 categories to match Order Queue tabs) */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Order Status</h3>
            <span className="text-sm text-gray-500">
              {currentDateTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
          </div>

          {/* Active Orders */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-all mb-3 group/active">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 group-hover/active:bg-blue-100 rounded-lg transition-colors">
                <Clock className="h-5 w-5 text-gray-400 group-hover/active:text-blue-600 transition-colors" />
              </div>
              <span className="text-gray-900 font-medium">Active Orders</span>
            </div>
            <span className="text-3xl font-bold text-gray-900">{formatNumber(stats.orders_active)}</span>
          </div>

          {/* Completed Today */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-all group/completed">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 group-hover/completed:bg-green-100 rounded-lg transition-colors">
                <CheckCircle className="h-5 w-5 text-gray-400 group-hover/completed:text-green-600 transition-colors" />
              </div>
              <span className="text-gray-900 font-medium">Completed Today</span>
            </div>
            <span className="text-3xl font-bold text-gray-900">{formatNumber(stats.orders_completed_today)}</span>
          </div>
        </motion.div>

        {/* Right Card: Menu Quick View */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="relative bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col"
        >
          {/* 3-dot menu button */}
          <button
            ref={editMenuButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsEditMenuDropdownOpen(!isEditMenuDropdownOpen);
            }}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isRebuildingCache}
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {/* Dropdown menu */}
          {isEditMenuDropdownOpen && (
            <div
              ref={editMenuDropdownRef}
              className="absolute top-14 right-4 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden"
            >
              <div className="py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditMenuDropdownOpen(false);
                    const restId = restaurantData?.rest_id || localStorage.getItem('current_rest_id');
                    navigate(`/menu-table-editor?processing_id=${activeMenu?.menu_id}&rest_id=${restId}`);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-100"
                >
                  <Table2 className="w-4 h-4 text-blue-600" />
                  <div>
                    <div className="text-gray-900 text-sm font-medium">Edit in Table</div>
                    <div className="text-gray-500 text-xs">Spreadsheet-style bulk editing</div>
                  </div>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditMenuDropdownOpen(false);
                    setShowCacheRebuildModal(true);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                >
                  <RefreshCw className="w-4 h-4 text-green-600" />
                  <div>
                    <div className="text-gray-900 text-sm font-medium">Refresh Menu</div>
                    <div className="text-gray-500 text-xs">Update menu data</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center gap-3 mb-1">
            <div className="bg-purple-100 p-2.5 rounded-xl">
              <UtensilsCrossed className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h4 className="text-gray-900 font-bold text-lg">Menu Quick View</h4>
              <p className="text-gray-500 text-sm">
                {activeMenu?.total_items || 0} items · {activeMenu?.menu_highlights?.section_count || 0} sections
              </p>
            </div>
          </div>

          {/* Metric blocks */}
          <div className="mt-4 grid grid-cols-3 gap-3 flex-grow">
            {/* Popular */}
            <button
              onClick={() => openHighlightModal('popular')}
              className="group/metric flex flex-col items-center p-4 rounded-xl bg-gray-50 border border-gray-200 hover:bg-amber-50 hover:border-amber-200 transition-all cursor-pointer"
            >
              <span className="text-3xl font-bold text-gray-900 mb-1">
                {activeMenu?.menu_highlights?.popular_count || 0}
              </span>
              <Star className="w-5 h-5 text-amber-500 mb-1" />
              <span className="text-xs font-medium text-gray-600">Popular</span>
              <span className="text-[10px] text-blue-600 font-medium mt-1 opacity-0 group-hover/metric:opacity-100 transition-opacity">
                Manage
              </span>
            </button>

            {/* Chef's Picks */}
            <button
              onClick={() => openHighlightModal('chef')}
              className="group/metric flex flex-col items-center p-4 rounded-xl bg-gray-50 border border-gray-200 hover:bg-orange-50 hover:border-orange-200 transition-all cursor-pointer"
            >
              <span className="text-3xl font-bold text-gray-900 mb-1">
                {activeMenu?.menu_highlights?.chef_count || 0}
              </span>
              <ChefHat className="w-5 h-5 text-orange-500 mb-1" />
              <span className="text-xs font-medium text-gray-600">Chef's Picks</span>
              <span className="text-[10px] text-blue-600 font-medium mt-1 opacity-0 group-hover/metric:opacity-100 transition-opacity">
                Manage
              </span>
            </button>

            {/* Spice Adjustable */}
            <button
              onClick={() => openHighlightModal('spice')}
              className="group/metric flex flex-col items-center p-4 rounded-xl bg-gray-50 border border-gray-200 hover:bg-red-50 hover:border-red-200 transition-all cursor-pointer"
            >
              <span className="text-3xl font-bold text-gray-900 mb-1">
                {activeMenu?.menu_highlights?.spice_count || 0}
              </span>
              <Flame className="w-5 h-5 text-red-500 mb-1" />
              <span className="text-xs font-medium text-gray-600">Spice Adj.</span>
              <span className="text-[10px] text-blue-600 font-medium mt-1 opacity-0 group-hover/metric:opacity-100 transition-opacity">
                Manage
              </span>
            </button>
          </div>

          {/* Edit Full Menu link */}
          <button
            onClick={handleEditMenu}
            className="flex items-center text-blue-600 font-medium mt-4 pt-3 border-t border-gray-100 hover:text-blue-800 transition-colors w-full"
          >
            <Edit className="w-4 h-4 mr-2" />
            <span>Edit Full Menu</span>
            <ArrowRight className="w-4 h-4 ml-auto" />
          </button>
        </motion.div>
      </div>

      {/* Order Queue (Step 7 Requirement) */}
      <OrderQueueCard />

      {/* FAQ Card - Restaurant-specific Q&A for AI Host */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-8 bg-white rounded-2xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-all"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <HelpCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Restaurant FAQs</h3>
              <p className="text-sm text-gray-500">
                {faqCount === 0
                  ? 'Help your AI answer accurately'
                  : `${faqCount} question${faqCount === 1 ? '' : 's'} configured`}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsFAQModalOpen(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Manage
          </button>
        </div>

        {/* Preview of top 3 FAQs or empty state */}
        {topFAQs.length > 0 ? (
          <div className="space-y-2 mt-4 pt-4 border-t border-gray-100">
            {(faqsExpanded ? allFAQs : topFAQs).map((faq) => (
              <div key={faq.faq_id} className="rounded-lg overflow-hidden">
                <div
                  className="flex items-center gap-2 text-sm text-gray-600 py-2 px-3 bg-gray-50 hover:bg-purple-50 transition-colors cursor-pointer"
                  onClick={() => setExpandedFaqId(expandedFaqId === faq.faq_id ? null : faq.faq_id)}
                >
                  <MessageSquare className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span className={expandedFaqId === faq.faq_id ? '' : 'line-clamp-1'}>{faq.question}</span>
                  {expandedFaqId === faq.faq_id
                    ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" />
                    : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" />
                  }
                </div>
                {expandedFaqId === faq.faq_id && faq.answer && (
                  <div className="px-3 pb-2 pt-1 bg-purple-50 text-xs text-gray-600 ml-6 mr-3 rounded-b-lg">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
            {faqCount > 3 && (
              <button
                onClick={() => setFaqsExpanded(!faqsExpanded)}
                className="text-xs text-purple-500 hover:text-purple-700 pl-6 font-medium transition-colors"
              >
                {faqsExpanded ? 'Show less' : `+${faqCount - 3} more FAQ${faqCount - 3 === 1 ? '' : 's'}`}
              </button>
            )}
          </div>
        ) : (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl">
              <div className="p-2 bg-purple-100 rounded-lg">
                <HelpCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-700">
                  Add FAQs to help your AI Host answer customer questions accurately.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  e.g., "Is Chicken 65 dry or wet?" → "By default, it's served dry..."
                </p>
              </div>
              <button
                onClick={() => setIsFAQModalOpen(true)}
                className="px-3 py-1.5 text-purple-600 hover:text-purple-700 font-medium text-sm whitespace-nowrap"
              >
                Add FAQs →
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Cards Section */}
      <div className="mt-8 mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Menu Filter Card */}
          <MenuFilterCard />

          {/* Events Card - AI-powered event inquiries */}
          <EventsCard />
        </div>
      </div>

      {/* Refresh Menu Confirmation Modal */}
      {showCacheRebuildModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <RefreshCw className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Refresh Menu?</h3>
            </div>

            <p className="text-gray-600 mb-6">
              This will update your menu with the latest information. Your menu will stay live during this process.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCacheRebuildModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRebuildCache}
                disabled={isRebuildingCache}
                className="flex-1 px-4 py-3 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
              >
                {isRebuildingCache ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  'Refresh Menu'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Pause Orders Modal */}
      {showAcceptingOrdersModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-8 max-w-lg w-full shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Pause Orders</h3>
            </div>

            <p className="text-gray-600 mb-6">
              Customers calling in will hear your message. You can optionally set a time to automatically resume accepting orders.
            </p>

            {/* Message Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message for Customers *
              </label>
              <textarea
                value={notAcceptingMessage}
                onChange={(e) => setNotAcceptingMessage(e.target.value)}
                placeholder="e.g., We're experiencing a power outage and expect to resume service after 10 PM."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 resize-none text-gray-800"
                rows={3}
              />
            </div>

            {/* Resume Time (Optional) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Auto-Resume (Optional)
                </div>
              </label>
              <input
                type="datetime-local"
                value={notAcceptingUntil ? notAcceptingUntil.slice(0, 16) : ''}
                onChange={(e) => setNotAcceptingUntil(e.target.value ? new Date(e.target.value).toISOString() : '')}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 text-gray-800"
              />
              <p className="text-gray-500 text-xs mt-1">
                Leave empty to resume manually. Orders will automatically resume at this time.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAcceptingOrdersModal(false);
                  setNotAcceptingMessage('');
                  setNotAcceptingUntil('');
                }}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePauseOrders}
                disabled={savingAcceptingOrders || !notAcceptingMessage.trim()}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
              >
                {savingAcceptingOrders ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Pause Orders'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
        </div>
      </div>

      {/* Footer with Legal Links - Break out of sidebar margin to span full width */}
      {/* Skip footer when hideHeader (VH Staff mode) - VHStaffDashboard renders its own full-width footer */}
      {!hideHeader && (
        <div
          className={`pt-6 pb-6 border-t border-gray-200 bg-white ${sidebarExpanded ? '-ml-72 pl-72' : '-ml-28 pl-28'}`}
          style={{ width: 'calc(100% + ' + (sidebarExpanded ? '18rem' : '7rem') + ')' }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-gray-500 text-sm">
                © {new Date().getFullYear()} Dine AI LLC. All rights reserved.
              </div>
              <div className="flex items-center gap-6 text-sm">
                <a
                  href="/terms"
                  target="_blank"
                  className="text-gray-500 hover:text-blue-600 transition-colors"
                >
                  Terms of Service
                </a>
                <a
                  href="/privacy"
                  target="_blank"
                  className="text-gray-500 hover:text-blue-600 transition-colors"
                >
                  Privacy Policy
                </a>
                <a
                  href="/cookies"
                  target="_blank"
                  className="text-gray-500 hover:text-blue-600 transition-colors"
                >
                  Cookie Policy
                </a>
                <a
                  href="/accessibility"
                  target="_blank"
                  className="text-gray-500 hover:text-blue-600 transition-colors"
                >
                  Accessibility
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Setup Wizard Modal - Steps 1-3 (Phone, Stripe, Done) */}
      <AIHostSetupWizard
        isOpen={isSetupWizardOpen}
        onClose={() => setIsSetupWizardOpen(false)}
        onComplete={() => {
          setIsSetupWizardOpen(false);
          fetchSetupStatus();
          toast.success('Setup completed! Your AI Host is now active.');
        }}
      />

      {/* Settings Page - Now a full page at /virtual-host/settings */}

      {/* Menu Highlights Toggle Modal */}
      {highlightModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl flex flex-col max-h-[80vh]"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {highlightModalCategory === 'popular' && 'Manage Popular Items'}
                {highlightModalCategory === 'chef' && "Manage Chef's Picks"}
                {highlightModalCategory === 'spice' && 'Manage Spice Adjustable'}
              </h3>
              <button
                onClick={() => setHighlightModalOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={highlightSearchText}
                onChange={(e) => setHighlightSearchText(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Item List */}
            <div className="overflow-y-auto flex-1 min-h-0 space-y-1">
              {(() => {
                const allItems = activeMenu?.menu_highlights?.all_items || [];
                const filtered = allItems.filter((item: any) =>
                  item.name.toLowerCase().includes(highlightSearchText.toLowerCase())
                );

                // Group by section
                const sections: Record<string, any[]> = {};
                filtered.forEach((item: any) => {
                  const sec = item.section || 'Other';
                  if (!sections[sec]) sections[sec] = [];
                  sections[sec].push(item);
                });

                return Object.entries(sections).map(([section, items]) => (
                  <div key={section} className="mb-3">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1">
                      {section}
                    </div>
                    {(items as any[]).map((item: any) => (
                      <label
                        key={item.id}
                        className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={highlightToggles[item.id] || false}
                          onChange={(e) => {
                            setHighlightToggles(prev => ({
                              ...prev,
                              [item.id]: e.target.checked
                            }));
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-900 flex-1">{item.name}</span>
                      </label>
                    ))}
                  </div>
                ));
              })()}
            </div>

            {/* Footer */}
            <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setHighlightModalOpen(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={saveHighlightToggles}
                disabled={savingHighlights}
                className="flex-1 px-4 py-2.5 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md text-sm"
              >
                {savingHighlights ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* FAQ Management Modal - Restaurant-specific Q&A for AI Host */}
      <FAQManagementModal
        isOpen={isFAQModalOpen}
        onClose={() => setIsFAQModalOpen(false)}
        onUpdate={() => {
          fetchFAQs();  // Refresh FAQ count and preview
        }}
      />
    </div>
  );
};

export default VirtualHostControlCenter;
